import dayjs from 'dayjs';
import { Accordion as BaseAccordian, Button } from 'react-bootstrap';
import { EllipsisMenu } from 'components';
import { FaCog } from 'react-icons/fa';
import { getDeviceListForGroup } from 'utils/iconUtils';
import { DeviceRowVertical } from './DeviceRows';
import { groupSearchFilter } from 'features/integrations/utils/integrationSearchUtils';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { useState } from 'react';


export default function Accordion({
  hasCreateEditPermission,
  hasDeletePermission,
  onEditGroup,
  onDeleteGroup,
  onOpenGroupSettings,
}) {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const { data: deviceGroups = [] } = useGetGroupsQuery();
  const [ellipsisOpenGroupId, setEllipsisOpenGroupId] = useState(null);

  // Mobile-only accordion view for connected device groups.
  return (
    <BaseAccordian className="d-block d-lg-none mt-3">
      {deviceGroups
        ?.filter((group) => groupSearchFilter(group, connectedDevices))
        .map((group, index) => (
          <BaseAccordian.Item
            key={group.id ?? index}
            eventKey={String(index)}
            className="bordered-table-accordion-item"
          >
            <BaseAccordian.Header>
              <div className="d-flex w-100 flex-column">
                <div className="d-flex w-100 justify-content-between">
                  <div className="flex-row flex-wrap">
                    <img
                      src={getDeviceListForGroup(group)}
                      alt={group?.type || 'RescAlert'}
                      width={'30px'}
                      height={'30px'}
                    />
                    <p className="h4 m-1 text-break">{group?.name}</p>
                  </div>
                  {(hasCreateEditPermission || hasDeletePermission) && (
                    <div className="d-flex align-items-center gap-2">
                      {hasCreateEditPermission && (
                        <span
                          role="button"
                          tabIndex={0}
                          className="btn btn-link p-0 text-white"
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpenGroupSettings?.(group?.id);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.stopPropagation();
                              onOpenGroupSettings?.(group?.id);
                            }
                          }}
                        >
                          <FaCog size={24} />
                        </span>
                      )}
                      <EllipsisMenu
                        containerClassname="bg-primary p-0"
                        open={ellipsisOpenGroupId === group?.id}
                        onOpen={() => setEllipsisOpenGroupId(group?.id)}
                        onClose={() => setEllipsisOpenGroupId(null)}
                      >
                        {hasCreateEditPermission && (
                          <Button
                            variant="primary"
                            className="w-100 text-center"
                            onClick={(event) => {
                              event.stopPropagation();
                              setEllipsisOpenGroupId(null);
                              onEditGroup(group?.id);
                            }}
                          >
                            Edit
                          </Button>
                        )}
                        {hasDeletePermission && (
                          <Button
                            variant="danger"
                            className=" w-100 text-center"
                            onClick={(event) => {
                              event.stopPropagation();
                              setEllipsisOpenGroupId(null);
                              onDeleteGroup(group);
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </EllipsisMenu>
                    </div>
                  )}
                </div>
                <div>
                  <p className="m-1">ID: {group.id}</p>
                </div>
                <div>
                  <p className="m-1">Devices: {group?.devices?.length}</p>
                </div>
                <div>
                  <p className="m-1">
                    Date Created: <br />
                    {dayjs(group?.date_created).format('MM/DD/YYYY, hh:mm:ss A')}
                  </p>
                </div>
              </div>
            </BaseAccordian.Header>

            {group?.devices?.length > 0 && (
              <BaseAccordian.Body className="bg-primary-shadow">
                {connectedDevicesLoading ? (
                  <div className="d-flex justify-content-center align-items-center py-3 text-secondary">
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Loading devices...
                  </div>
                ) : connectedDevicesError ? (
                  <div className="p-2 text-center text-danger">Unable to load devices.</div>
                ) : (
                  group.devices.map((device, dIndex) => (
                    <DeviceRowVertical
                      key={group.id + '-' + dIndex}
                      deviceId={device}
                      last={dIndex === group.devices.length - 1}
                    />
                  ))
                )}
              </BaseAccordian.Body>
            )}
          </BaseAccordian.Item>
        ))}
    </BaseAccordian>
  );
}
